﻿import { Types } from '../core/Enums';

/** Cast object to type without check */
export function toUnsafeType<T>(obj: unknown): T { return (obj as unknown) as T; }
/** Cast object to type if he expand it or return undefined if not */
export function toType<T>(obj: unknown, type: { new(...args: any[]): T }): T | undefined {
    return obj instanceof type ? obj as T : undefined;
}


export function hasProp(wr: object, propName: string, type?: Types) {
    const value = (wr as Record<string, unknown>)[propName];
    if (value===undefined) return false;
    if (type) {
        if (type === Types.NotFunction) {
            if ((typeof value === Types.Function) || (typeof value === Types.Undefined)) return false
            return true;
        }
        if (typeof value !== type) return false;
    }
    return true;    
}

/** Cast argument to integer or return default */
export function int(arg: any, def = 0): number {
    if (arg === undefined || arg === null || arg === '') return def;
    const value = parseInt(String(arg), 10);
    return Number.isFinite(value) ? value : def;
}
/** Cast argument to float or return default */
export function float(arg: any, def = 0.0): number {
    if (arg === undefined || arg === null || arg === '') return def;
    const value = parseFloat(String(arg));
    return Number.isFinite(value) ? value : def;
}
/** Cast argument to string or return default */
export function string(arg: any, def = ''): string {
    return arg === undefined || arg === null ? def : String(arg);
}
/** Cast argument to boolean or return default */
export function bool(arg: any, def = false): boolean {
    if (arg === undefined || arg === null) return def;
    if (typeof arg === 'boolean') return arg;
    const value = String(arg).trim().toLowerCase();
    if (value === 'true' || value === '1' || value === 't') return true;
    if (value === 'false' || value === '0' || value === 'f') return false;
    return def;
}
/** Cast argument to Date or return default */
export function date(arg: any, def = new Date()) {
    if (arg === undefined || arg === null || arg === '') return def;
    const value = new Date(arg);
    return Number.isNaN(value.getTime()) ? def : value;
}
/** Cast argument to enum:T or return default */
export function enumCast<T extends Record<string, string | number>>(
    enumType: T,
    arg: any,
    def?: T[keyof T]
): T[keyof T] | undefined {
    if (typeof arg === 'number'&&enumType[arg as keyof T] !== undefined) return arg as T[keyof T];
    if (typeof arg === 'string') {
        if (arg in enumType) { 
            const result = enumType[arg as keyof T];
            if (typeof result === 'number' || typeof result === 'string')  return result as T[keyof T];
        }
        const parsedNum = parseInt(arg, 10);
        if (!isNaN(parsedNum) && enumType[parsedNum as keyof T] !== undefined) return parsedNum as T[keyof T];
    }
    return def;
}

/** Trying found arg of object in various cases */
export function argSearch(name: string, obj: object | null | undefined): unknown {
    if (!obj) return undefined;
    const values = obj as Record<string, unknown>;
    const nameCap = name.charAt(0).toUpperCase() + name.slice(1);
    const nameLow = name.toLowerCase();
    for (const candidate of [name, nameCap, nameLow])
        if (Object.prototype.hasOwnProperty.call(values, candidate))
            return values[candidate];
    return undefined;
}
/** Search and cast object argument to integer */
export function argInt(name: string, obj: any, def = 0): number { const arg = argSearch(name, obj); return int(arg, def); }
/** Search and cast object argument to float */
export function argFloat(name: string, obj: any, def = 0.0): number { const arg = argSearch(name, obj); return float(arg, def); }
/** Search and cast object argument to string */
export function argString(name: any, obj: any, def = ''): string { const arg = argSearch(name, obj); return string(arg, def); }
/** Search and cast object argument to string */
export function argBool(name: any, obj: any, def = false) { const arg = argSearch(name, obj); return bool(arg, def); }
/** Search and cast object argument to string */
export function argDate(name: any, obj: any, def = new Date()) { const arg = argSearch(name, obj); return date(arg, def); }
/** Search and cast object argument to string */
export function argEnum<T extends Record<string, string | number>>(
    name: any,
    obj: any,
    enumType: T,
    def?: T[keyof T]
): T[keyof T] | undefined {
    const arg = argSearch(name, obj); 
    return enumCast(enumType, arg, def); 
}

